import React, { useEffect, useState, useRef } from "react";

// Components
import moment from "moment";
import { Modal, Tooltip, Menu, Grid, useTheme, useMediaQuery, Box } from "@mui/material";
import { toast } from "react-toastify";

// Custom components
import { SearchBarCompanyWorkers } from "../../components/atoms/searchBars/SearchBarCompanyWorkers";
import TableCompanyWorkers from "../../components/atoms/tables/TableCompanyWorkers";
import { ShowDialog } from "../../components/ShowDialog";
import { BackdropLoading } from "../../components/BackdropLoading";
import { CompanyWorkerModalCard } from "../../components/atoms/modalCards/CompanyWorkerModalCard";
import { GroupedChipsWithTooltip } from "../../components/atoms/GroupedChipsWithTooltip";

// Services
import { CompanyWorkerApi } from "../../services/CompanyWorkerApi";
import { WorkerCertificateApi } from "../../services/WorkerCertificateApi";

// Styles
import { BoxModal, Container, CustomButtonV2 } from "../styles";
import { CustomMenuItem, PageTitle, StyledChip } from "./styles";

// Icons
import { AddCircleOutlineRounded as AddCircleOutlineRoundedIcon, DescriptionOutlined as DescriptionOutlinedIcon, SaveAltOutlined as SaveAltOutlinedIcon, KeyboardArrowDown as KeyboardArrowDownIcon, CheckCircleOutline, DescriptionOutlined, FileUploadOutlined, ErrorOutlineRounded, HighlightOffOutlined } from "@mui/icons-material";

// Utils
import { useTranslation } from 'react-i18next'
import { CheckResponse } from "../../utils/checkResponse";
import { validateFiles, returnStringCertificateNames } from "../../utils/commonFunctions";
import { arrayToString } from "../../utils/commonFunctions/convert";

// Hooks
import { useDownloadExcel } from "../../Hooks/useDownloadExcel";

// Permissions
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../Context/PermissionsContext'
import { FormatDate } from "../../utils/formatDate";

// FakeDate TODO: dcuellar
//import { data as fakeData, arrayArl as arl, arrayEps as eps } from './dataFake';

function ChipWithTooltip({ label, expirationDate, isSpecial, isError, isInvalid, icon, disableTooltip }) {
  if (!label || String(label).trim() === '') return null;

  const composedLabel = (
    <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
      <span>{label}</span>
      {(expirationDate && (isSpecial || isInvalid)) && (
        <span style={{ fontSize: '10px', opacity: 0.75 }}>
          {moment(expirationDate).format("DD/MM/YYYY")}
        </span>
      )}
    </span>
  );

  const chip = (
    <StyledChip
      label={composedLabel}
      isSpecial={isSpecial}
      isError={isError}
      isInvalid={isInvalid}
      icon={icon}
    />
  );
  return disableTooltip ? chip : (
    <Tooltip title={label} placement="top">
      {chip}
    </Tooltip>
  );
}

function Certification(row) {
  const data = row.row;

  if (!data) return null;
  const dataArray = Array.isArray(data) ? data : [data];
  if (dataArray.length === 0) return <span></span>;

  const getPropsObj = (el) => {
    const isSpecial = el.trainingStatus === true;
    const isError = el.trainingStatus === false;

    let icon = undefined;
    if (isSpecial) icon = <CheckCircleOutline sx={{ fontSize: "16px", color: "inherit" }}/>;
    else if (isError) icon = <ErrorOutlineRounded sx={{ fontSize: "16px", color: "inherit" }}/>;

    return {
      label: el.name,
      expirationDate: el?.expirationDate ?? null,
      isSpecial,
      isError,
      isInvalid: undefined,
      icon
    };
  };

  return (
    <GroupedChipsWithTooltip 
      data={dataArray} 
      getPropsObj={getPropsObj} 
    />
  );
}

function CertificateRoles(row) {
  const data = row.certificatesRoles;
  
  if (!data || data.length === 0) return null;

  const getPropsObj = (el, index) => {
    const isError = el.hasError === true;
    const isInvalid = el.certificationDate !== null && el.isValid === false && el.hasError === false;

    let isSpecial;
    let icon = undefined;

    if (data.length === 1) {
      isSpecial = el.certificationDate !== null && el.isValid === true && el.hasError === false;
      if (isError) icon = <HighlightOffOutlined sx={{ fontSize: "16px", color: "inherit" }}/>;
      else if (isInvalid) icon = <ErrorOutlineRounded sx={{ fontSize: "16px", color: "inherit" }}/>;
      else if (el.certificationDate !== null && el.hasError === false) icon = <CheckCircleOutline sx={{ fontSize: "16px", color: "inherit" }}/>;
    } else {
      if (index === 0) {
        isSpecial = el.certificationDate !== null && el.hasError === false;
        if (isError) icon = <HighlightOffOutlined sx={{ fontSize: "16px", color: "inherit" }}/>;
        else if (isInvalid) icon = <ErrorOutlineRounded sx={{ fontSize: "16px", color: "inherit" }}/>;
        else if (el.certificationDate !== null && el.isValid === true && el.hasError === false) icon = <CheckCircleOutline sx={{ fontSize: "16px", color: "inherit" }}/>;
      } else {
        isSpecial = el.certificationDate !== null && el.hasError === false;
        if (isError) icon = <HighlightOffOutlined sx={{ fontSize: "16px", color: "inherit" }}/>;
        else if (isInvalid) icon = <ErrorOutlineRounded sx={{ fontSize: "16px", color: "inherit" }}/>;
        else if (isSpecial) icon = <CheckCircleOutline sx={{ fontSize: "16px", color: "inherit" }}/>;
      }
    }

    return {
      label: el.name,
      expirationDate: el?.expirationDate ?? null,
      isSpecial,
      isError,
      isInvalid,
      icon
    };
  };

  return <GroupedChipsWithTooltip data={data} getPropsObj={getPropsObj} />;
}

function EpsArlChip({ data, fallback }) {
  if (!data && !fallback) return null;
  if (!data && fallback) return <div>{fallback}</div>;

  let isSpecial = false;
  let isError = false;

  const status = data.epsStatus !== undefined ? data.epsStatus : data.arlStatus;
  
  if (status === "1" || status === 1) {
    isSpecial = true;
  } else if (status === "2" || status === 2) {
    isError = true;
  }

  return (
    <ChipWithTooltip
      label={data.name}
      isSpecial={isSpecial}
      isError={isError}
      icon={isSpecial ? <CheckCircleOutline sx={{ fontSize: "16px", color: "inherit" }}/> : isError ? <ErrorOutlineRounded sx={{ fontSize: "16px", color: "inherit" }}/> : undefined}
    />
  );
}


export const CompanyWorker = (props) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [anchorEl, setAnchorEl] = useState(null);
  const fileInputRef = useRef(null);

  const handleUploadClick = () => {
    // Handle upload action
    fileInputRef.current.click();
  };

  const handleDownloadClick = () => {
    // Handle download action
    handleDownloadTemplate();
    handleClose();
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { t } = useTranslation();
  const ability = useAbility(AbilityContext);

  const companyWorkerApi = new CompanyWorkerApi();
  const workerCertificateApi = new WorkerCertificateApi();
  const { check } = new CheckResponse();
  const { format } = new FormatDate();

  const [isEdit, setIsEdit] = useState(false);
  const [arrayEps, setArrayEps] = useState("");
  const [arrayArl, setArrayArl] = useState("");
  const [data, setData] = useState([]);
  const [dataCertificates, setDataCertificates] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filteredRequest, setFilteredRequest] = useState([]);
  const [finalDate, setFinalDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );
  const [initialDate, setInitialDate] = useState(
    moment(finalDate).subtract(1, "months").format("YYYY-MM-DD")
  );
  const [dialog, setDialog] = useState({
    status: false,
    title: "",
    message: "",
    type: null,
  });

  const firstLoad = React.useRef(true);
  const clearFilter = React.useRef(false);
  const searchText = React.useRef("");
  const pageRef = React.useRef({
    page: 0,
    pageSize: 25,
    rowsCount: 0,
    fullRowsCount: 0
  });

  const initialErrors = {
    identification: { error: '', field: t('worker.table.identification'), required: true },
    identificationDate: { error: '', field: t('worker.table.dateIssue'), required: true },
    name: { error: '', field: t('worker.table.name'), required: true },
    lastName: { error: '', field: t('worker.table.lastName'), required: true },
    eps: { error: '', field: t('worker.table.eps'), required: false },
    arl: { error: '', field: t('worker.table.arl'), required: false },
    emergencyContactFullName: { error: '', field: t('worker.table.emergencyContactFullName'), required: false },
    emergencyContactPhone: { error: '', field: t('worker.table.emergencyContactPhone'), required: false },
    authorizationPersonalData: { error: '', field: t('worker.authorizationPersonalData'), required: true },
    arlSelect: {},
    epsSelect: {},
    arlStatus: '',
    epsStatus: '',
  };

  const [errors, setErrors] = useState(initialErrors);

  function resetErrors() {
    setErrors(initialErrors);
  }


  const [openCompanyWorkerModal, setOpenCompanyWorkerModal] = useState(false);
  const [detail, setDetail] = useState({});

  const [headers, setHeaders] = useState([]);

  useEffect(() => {
    async function call() {
      firstLoad.current = true;
      await handleClearFilters(true);
    }
    call();
  }, []);

  const fieldsConfig = {
    [t('worker.table.id')]: 'workerId',
    [t('worker.table.identification')]: 'identification',
    [t('worker.table.name')]: 'name',
    [t('worker.table.lastName')]: 'lastName',
    [t('worker.table.eps')]: (item) => item.epsList.name || "",
    [t('worker.table.arl')]: (item) => item.arlList.name || "",
    [t('worker.table.certificates')]: (item) => returnStringCertificateNames(item.certificates),
    [t('worker.table.company')]: (item) => item.company.name,
    [t('worker.table.specialty')]: (item) => item.workerRole?.name || "",
    [t('worker.table.assignations')]: (item) => arrayToString(item.assignations, "name"),
    [t('worker.table.eva')]: (item) => arrayToString(item.assignationsRoles, "name"),
    [t('worker.table.email')]: (item) => item.email || "",
    [t('worker.table.authorizationPersonalData')]: (item) => item.authorizationPersonalData ? t('common.yes') : t('common.no'),
    [t('worker.table.dateIssue')]: (item) => item.expeditionDate,
    [t('worker.table.isActive')]: (item) => item.isActive ? t('common.yes') : t('common.no'),
  };

  const { handleDownloadExcelList } = useDownloadExcel(filteredRequest, fieldsConfig, t('worker.title'));

  const returnHeaders = (data) => {
    let heads = [
      {
        id: "identification",
        label: "worker.table.identification",
        isRender: true,
        center: false,
        sortArrow: false,
        sticky: true,
        direction: 'left',
      },
      {
        id: "name",
        label: "worker.table.name",
        isRender: true,
        center: false,
        sortArrow: false
      },
      {
        id: "lastName",
        label: "worker.table.lastName",
        isRender: true,
        center: false,
        sortArrow: false
      },
      {
        id: "epsSelect",
        label: "worker.table.eps",
        center: false,
        isRender: false,
        sortArrow: false,
        format: (row) => <EpsArlChip data={row.epsList} fallback={row.eps} />
      },
      {
        id: "arlSelect",
        label: "worker.table.arl",
        center: false,
        isRender: false,
        sortArrow: false,
        format: (row) => <EpsArlChip data={row.arlList} fallback={row.arl} />
      },
      {
        id: "certificate",
        label: "worker.table.certificates",
        type: "certificatesRoles",
        center: false,
        sortArrow: false,
        format: (row) => <CertificateRoles {...row} />,
      },
      // {
      //   id: "specialty",
      //   label: "worker.table.specialty",
      //   center: false,
      //   sortArrow: true,
      //   format: (value) => value.workerRole?.name || "",
      //   customSort: "workerRole?.name",
      // },
      // {
      //   id: "assignations",
      //   label: "worker.table.assignations",
      //   center: false,
      //   sortArrow: true,
      //   format: (row) => <Certification row={row.assignations} />,
      // },
      // {
      //   id: "eva",
      //   label: "worker.table.eva",
      //   center: false,
      //   sortArrow: true,
      //   format: (row) => <Certification row={row.assignationsRoles} />,
      // },
      {
        id: "email",
        label: "worker.table.email",
        center: false,
        sortArrow: true,
        format: (row) => row.email || "",
      },
      {
        id: "emergencyContactFullName",
        label: "worker.table.emergencyContactFullName",
        isRender: true,
        center: false,
        sortArrow: false
      },
      {
        id: "emergencyContactPhone",
        label: "worker.table.emergencyContactPhone",
        isRender: true,
        center: false,
        sortArrow: false
      },
      {
        id: "dateIssue",
        label: "worker.table.dateIssue",
        center: false,
        sortArrow: false,
        format: (row) => moment(row.expeditionDate).format("DD/MM/YYYY"),
      },
      {
        id: "company",
        label: "worker.table.company",
        center: false,
        format: (value) => value.company?.name,
        customSort: "company?.name",
        sortArrow: false
      },
      {
        id: "isActive",
        label: "worker.table.isActive",
        center: false,
        sortArrow: true,
        format: (value) => value.isActive ? t('common.yes') : t('common.no'),
        customSort: "isActive ? 'Si' : 'No'",
      }
    ];

    let newActions = [];
    if (ability.can("UpdateWorkers")) {
      newActions.push('edit');
    }
    if (ability.can("DeleteWorkers")) {
      newActions.push('delete');
    }

    if (newActions.length > 0) {
      heads.push(addActionsRow(newActions))
    }

    setHeaders(heads);
  }

  function addActionsRow(actions) {
    return {
      id: "actions",
      label: "common.actions.title",
      type: "actions",
      center: false,
      sortArrow: false,
      sticky: true,
      direction: 'right',
      keys: actions,
    }
  }



  async function loadData() {
    setLoading(true);

    await getCertificatesWithRoles();

    let response = await companyWorkerApi.list({
      isEnabled: true,
      currentPage: pageRef.current.page + 1,
      pageSize: pageRef.current.pageSize,
      filter: searchText.current
    });

    if (response.status === 200) {
      let dataResp = response.data;


      pageRef.current.rowsCount = dataResp.rowsCount;
      pageRef.current.fullRowsCount = dataResp.rowsCount;


      let arrayData = await formatResponse(dataResp)
      setLoading(false);
      firstLoad.current = false;

      return { hasData: true, data: arrayData };
    }

    setLoading(false);
    return { hasData: false, data: [] };
  }

  async function getCertificatesWithRoles() {
    const response = await workerCertificateApi.getCertificatesWithRoles();
    if (([201, 200].includes(response.status))) {
      response.data.forEach(cert => cert.selectedOptions = []);
      setDataCertificates(response.data)
    }
  }

  function handleEdit(row) {
    setIsEdit(true);
    setOpenCompanyWorkerModal(true)

    if (!row.identificationDate) {
      setErrors({
        ...errors,
        identificationDate: {
          ...errors.identificationDate,
          error: '',
          required: false,
        },
        authorizationPersonalData: {
          ...errors.authorizationPersonalData,
          error: '',
          required: false,
        }
      });
    }

    if (row.identificationDate) {
      setErrors({
        ...errors,
        identificationDate: {
          ...errors.identificationDate,
          required: true,
        },
        authorizationPersonalData: {
          ...errors.authorizationPersonalData,
          required: true,
        }
      });
    }

    setDetail(row);
  }

  function handleCreate() {
    setOpenCompanyWorkerModal(true)
    setDetail({})
  }

  async function handleDownloadTemplate() {
    setLoading(true);

    let response = await companyWorkerApi.template();
    let result = check(response)

    if (result.status) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Plantilla listado trabajadores.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    }
    else {
      toast.error(() =>
        <div>{t('worker.messages.template.error')}<br />
          {result.errors}
        </div>
      );
    }
    setLoading(false);
  }

  async function handleSaveUploadFile(event) {
    let file = event?.target.files[0]

    if (file != null) {
      setLoading(true)
      let resultCheck = await validateFiles([file])
      if (!resultCheck) {
        setLoading(false)
        return
      }

      setLoading(true);
      let formData = new FormData()
      formData.append('file', file)

      let response = await companyWorkerApi.upload(formData);
      let result = check(response)

      if (result.status) {
        setDialog({
          status: true,
          title: t('worker.messages.save.title'),
          message: t('worker.messages.save.message'),
          type: null,
        });
        await loadData();
      }
      else {
        toast.error(() =>
          <div>{t('worker.messages.save.error')}<br />
            {result.errors}
          </div>
        );
      }
      setLoading(false);

    }
    // Restablece el valor del input de archivo a null
    event.target.value = null;
  }

    async function handleSaveUploadCertificate(event) {
    let file = event?.target.files[0]

    if (file != null) {
      setLoading(true)
      let resultCheck = await validateFiles([file])
      if (!resultCheck) {
        setLoading(false)
        return
      }

      setLoading(true);
      let formData = new FormData()
      formData.append('file', file)

      let response = await companyWorkerApi.validateCertificate(formData);
      let result = check(response)

      if (result.status) {
        setDialog({
          status: true,
          title: t('worker.messages.save.title'),
          message: t('worker.messages.save.message'),
          type: null,
        });
        await loadData();
      }
      else {
        toast.error(() =>
          <div>{t('worker.messages.save.error')}<br />
            {result.errors}
          </div>
        );
      }
      setLoading(false);

    }
    // Restablece el valor del input de archivo a null
    event.target.value = null;
  }

  function validateRetieTechnicalCertificate(data) {
    // Validar que se hayan seleccionado al menos una clase y un archivo
    if (data.certificateRoleElectricalLevels?.length === 0) {
      toast.error(t('worker.messages.save.errorElectricalLevels'));
      return false
    }
    if (!data.fileName) {
      toast.error(t('worker.messages.save.errorFile'));
      return false
    }

    return true
  }

  function formatData(data, isRetieTechnicalCertificate) {
    const certificateIds = data.certificates.flatMap(cert => cert.selectedOptions.map(sel => sel.id));

    let body = {
      identification: data.identification,
      identificationDate: moment(data.identificationDate).format("YYYY-MM-DD"),
      name: data.name,
      lastName: data.lastName,
      eps: data.eps?.value !== 0 ? data.eps?.value : null,
      arl: data.arl?.value !== 0 ? data.arl?.value : null,
      email: data.email,
      emergencyContactFullName: data.emergencyContactFullName,
      emergencyContactPhone: data.emergencyContactPhone,
      certificateRoleIds: certificateIds,
      certificateRoleElectricalLevels: null,
      workerRoleId: data.workerRoleId?.value ?? data.workerRoleId?.id,
      assignations: data.assignations?.map((el) => el.id),
      authorizationPersonalData: data.authorizationPersonalData

    }

    // Se agrega al body el array de clases y el archivo si la condición es true
    const formData = new FormData();

    if (isRetieTechnicalCertificate) {
      if (!validateRetieTechnicalCertificate(data)) return

      const roleElectricalLevels = data.certificateRoleElectricalLevels.map((el) => el.id);
      body.certificateRoleElectricalLevels = roleElectricalLevels
      formData.append('file', data.file);
    }

    // EPS
    if (data.epsFile) {
      formData.append('epsCertificate', data.epsFile);
    }

    // ARL
    if (data.arlFile) {
      formData.append('arlCertificate', data.arlFile);
    }

    formData.append('data', JSON.stringify(body));

    return formData;
  }

  async function handleSaveEdit(data, isRetieTechnicalCertificate) {
    const formData = formatData(data, isRetieTechnicalCertificate);

    setLoading(true);

    let response = null
    if (data.id) response = await companyWorkerApi.update(data.id, formData);
    else response = await companyWorkerApi.create(formData);

    let result = check(response)

    if (result.status) {
      setOpenCompanyWorkerModal(false);
      setDialog({
        status: true,
        title: t('worker.messages.save.title'),
        message: t('worker.messages.save.message'),
        type: null,
      });
      await loadData();
    }
    else {
      toast.error(() =>
        <div>{t('worker.messages.save.error')}<br />
          {result.errors}
        </div>
      );
    }
    setLoading(false);
  }


  function generateNaturalWorkerId() {
    const MAX_VALUE = 1000000; // Rango máximo, ajustable según tu necesidad
    return Math.floor(Math.random() * MAX_VALUE) + 1; // Siempre un número natural positivo
  }

  async function handleDelete() {
    if (!dialog.row.workerId) {
      return
    }

    setLoading(true);
    let response = await companyWorkerApi.delete(dialog.row.workerId);
    let result = check(response)

    if (result.status) {
      handleCloseModalDelete();
      setDialog({
        status: true,
        title: t('worker.messages.delete.title'),
        message: t('worker.messages.delete.message'),
        type: null,
      });
      await loadData();
    }
    else {
      toast.error(() =>
        <div>{t('worker.messages.delete.error')}<br />
          {result.errors}
        </div>
      );
    }
    setLoading(false);
  }

  async function handleOpenModalDelete(row) {
    setDialog({
      status: true,
      title: t('worker.messages.delete.confirm'),
      message: t('worker.messages.delete.confirmMessage', { worker: `${row.name} ${row.lastName}` }),
      type: "confirmation",
      row: row
    });
  }

  async function handleCloseModalDelete(confirmDelete) {
    if (confirmDelete) {
      setDialog({ ...dialog, status: false });
      await handleDelete()
    } else {
      setDialog({ status: false });
    }
  }

  async function handleChangePage(page, pageSize, rowsCount, isFilter) {
    pageRef.current.page = page;
    pageRef.current.pageSize = pageSize || 25;
    pageRef.current.rowsCount = rowsCount || pageRef.current.fullRowsCount;
    if (!isFilter) return await loadData();
    return []
  };

  const handleChangeFilter = async () => {
    pageRef.current.page = 0;
    firstLoad.current = true;
    await loadData();
  };

  async function handleFilters(e) {
    e?.preventDefault();
    firstLoad.current = true;
    await handleChangePage(0, pageRef.current.pageSize);
  }

  const chunk = (arr, size) =>
    Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
      arr.slice(i * size, i * size + size)
    );

  async function formatResponse(currentData) {
    let arrayData = [];

    const arrayConWorkerId = currentData.results.map(({ id, ...attrs }) => ({
      workerId: id, // Renombramos id a workerId
      ...attrs      // Conservamos el resto de las propiedades
    }));

    //const arrayConWorkerId = fakeData;

    if (data.length === 0 || firstLoad.current) {
      arrayData = arrayConWorkerId;
    }
    else {
      // Dividir filteredRequest en bloques de datos de pageSize
      let newArray = chunk(data, pageRef.current.pageSize);

      // Validar si la data de currentpage ya existe en filteredRequest
      // Si no existe se agrega la nueva data a filteredRequest
      if (newArray[currentData.currentPage - 1] === undefined) {
        arrayData = [...data, ...arrayConWorkerId];
      }
      // Si ya existe se busca la posición y se reemplaza con la nueva data
      else {
        // Mantener el orden de las páginas anteriores y agregar los nuevos datos
        newArray[currentData.currentPage - 1] = arrayConWorkerId;

        // Aplana el array después de reemplazar la página correcta, pero respeta el orden original
        arrayData = [].concat(...newArray);
      }
    }

    if (firstLoad.current) {
      returnHeaders(arrayData);
      //returnHeaders(fakeData);
    }

    setData(arrayData);
    setArrayArl(arrayData.arl)
    setArrayEps(arrayData.eps)
    //setData(fakeData);
    setFilteredRequest(arrayData);
    //setFilteredRequest(fakeData);
    return arrayData;
  }

  async function handleClearFilters(isChangeWorkflow) {
    setFilteredRequest([]);
    setData([]);
    firstLoad.current = true;
    let pageSize = isChangeWorkflow === true ? 25 : pageRef.current.pageSize
    pageRef.current = { page: 0, pageSize: pageSize, rowsCount: 0, fullRowsCount: 0 };

    clearFilter.current = !clearFilter.current;
    await loadData();
  }

  const handleCloseModal = () => {
    setOpenCompanyWorkerModal(false)
    setIsEdit(false);
    resetErrors();
  }

  return (
    <Container open={props.open} style={{ marginBottom: 0 }}>
      <Grid container 
        spacing={1}
        alignItems="center"
        sx={{ mb: { md: 2 } }}
        columns={{ xs: 12, sm: 12, md: 12, lg: 23, xl: 12 }}
      >
        <Grid item xs={12} md={12} lg={6} xl={4}>
          <PageTitle>
            {t('worker.title')}
          </PageTitle>
        </Grid>
        <Grid item xs={12} md={6} lg={8} xl={4}>
          <SearchBarCompanyWorkers
            handleChangeFilter={handleChangeFilter}
            handleFilters={handleFilters}
            searchText={searchText}
            clearFilter={clearFilter}
          />
        </Grid>
        {isDesktop &&
          <Grid item md={false} lg={1}></Grid>
        }
        <Grid item xs={12} md={6} lg={8} xl={3}
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>

          {ability.can("CreateWorkers") && (
            <CustomButtonV2
              btnType="primary"
              onClick={handleCreate}
              startIcon={<AddCircleOutlineRoundedIcon />}
              sx={{ width: "110%" }}
            >
              {t('worker.modal.create')}
            </CustomButtonV2>
          )}

          <Box sx={{ width: '100%' }}>
            <CustomButtonV2
              btnType="primary"
              onClick={handleOpen}
              startIcon={<DescriptionOutlinedIcon />}
              endIcon={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '1px', height: '16px', backgroundColor: 'rgba(255, 255, 255, 0.4)', mx: 0.5, mr: 1 }} />
                  <KeyboardArrowDownIcon />
                </Box>
              }
            >
              {t('worker.template')}
            </CustomButtonV2>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <CustomMenuItem onClick={handleDownloadClick}>
                <DescriptionOutlined/>
                {t('worker.downloadTemplate')}
              </CustomMenuItem>
              <CustomMenuItem onClick={handleUploadClick}>
                <FileUploadOutlined/>
                {t('worker.uploadFile')}
                <input
                  type="file"
                  hidden
                  onChange={handleSaveUploadFile}
                  ref={fileInputRef}
                  accept=".xlsx"
                  onClick={(e) => e.stopPropagation()}
                />
              </CustomMenuItem>
            </Menu>
          </Box>

          <CustomButtonV2
            onClick={handleDownloadExcelList}
            startIcon={<SaveAltOutlinedIcon />}
          >
            {t('common.download')}
          </CustomButtonV2>
        </Grid>
      </Grid>

      <TableCompanyWorkers
        headers={headers}
        filteredRequest={filteredRequest}
        // rows={data}
        rows={data}
        // Manage date
        initialDate={initialDate}
        setInitialDate={setInitialDate}
        finalDate={finalDate}
        setFinalDate={setFinalDate}
        // Handle edit, delete, process
        handleEdit={handleEdit}
        handleDelete={handleOpenModalDelete}
        hideFilterDate={true}
        // Paginator data
        isCustom={true}
        onChangePage={handleChangePage}
        rowsCount={pageRef.current.rowsCount}
        currentPage={pageRef.current.page}
        pageSize={pageRef.current.pageSize}
      />

      <ShowDialog
        openDialog={dialog.status}
        dialog={dialog}
        handleCloseDialog={handleCloseModalDelete}
      />

      <Modal open={openCompanyWorkerModal}>
        <BoxModal>
          <CompanyWorkerModalCard
            isEdit={isEdit}
            errorsCard={errors}
            setErrorsCard={setErrors}
            data={detail}
            dataCertificates={dataCertificates}
            handleSave={handleSaveEdit}
            handleClose={handleCloseModal}
          />
        </BoxModal>
      </Modal>

      <BackdropLoading open={loading} />

    </Container>
  );
};